import { authState } from '../../lib/auth.js';
import { getBackendUrl } from '../../lib/backend.js';
import { BaseComponent } from '../../lib/base-component.js';
import { IDENTITY_MISMATCH, invitationIdentity } from '../../lib/invitation-identity.js';
import {
    navigateAfterSignIn,
    SIGN_IN_PATH,
    saveInvitationDestination,
    saveInvitationReturn,
} from '../../lib/pending-oauth.js';
import { css, html } from '../../vendor/lit.min.js';

class InvitationView extends BaseComponent {
    static properties = {
        _invitation: { type: Object, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _username: { type: String, state: true },
        _submitting: { type: Boolean, state: true },
        _code: { type: String, state: true },
        _codeSent: { type: Boolean, state: true },
        // Who is reading the page, which the invitation record cannot say (#105).
        _authSettled: { type: Boolean, state: true },
        _authenticated: { type: Boolean, state: true },
        _currentEmail: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .invitation-card { max-width: 480px; margin: 0 auto; }
        .consent-block {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 0.375rem;
            padding: 0.75rem 1rem;
        }
        .consent-block ul { padding-left: 1.1rem; }
        .consent-block li + li { margin-top: 0.35rem; }
    `;

    constructor() {
        super();
        this._invitation = null;
        this._loading = true;
        this._error = '';
        this._username = '';
        this._submitting = false;
        this._code = '';
        this._codeSent = false;
        this._authSettled = false;
        this._authenticated = false;
        this._currentEmail = '';
    }

    async connectedCallback() {
        super.connectedCallback();
        this._loadInvitation();

        // The page has to know who is reading it before it offers them anything
        // (#105). Read the current state as well as subscribing, because auth
        // may already have settled by the time this view mounts and a
        // subscription alone would then never fire.
        this._applyAuthState(authState);
        this._unsubscribeAuth = authState.subscribe((state) => this._applyAuthState(state));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._unsubscribeAuth?.();
    }

    _applyAuthState(state) {
        this._authSettled = !state.loading;
        this._authenticated = Boolean(state.isAuthenticated);
        this._currentEmail = state.user?.email || '';
    }

    async _loadInvitation() {
        const path = window.location.pathname;
        const match = path.match(/^\/invitation\/([^/]+)/);
        if (!match) {
            this._error = 'Invalid invitation link';
            this._loading = false;
            return;
        }

        try {
            const response = await fetch(`${getBackendUrl()}/api/v1/invitation/${match[1]}`);
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                this._error = data.message || 'Invitation not found';
                this._loading = false;
                return;
            }
            this._invitation = await response.json();
        } catch {
            this._error = 'Failed to load invitation';
        }
        this._loading = false;
    }

    _handleSubmit(e) {
        e.preventDefault();
        if (!this._codeSent) {
            this._sendCode();
        } else {
            this._verifyAndAccept();
        }
    }

    // _sendCode emails a one-time code to the invited address so the acceptor
    // can prove they own it before an account is created.
    async _sendCode() {
        this._error = '';
        this._submitting = true;
        try {
            const response = await fetch(`${getBackendUrl()}/api/v1/send-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: this._invitation.email,
                    tenant_id: this._invitation.tenant_id,
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                this._error = data.message || 'Failed to send verification code';
                this._submitting = false;
                return;
            }
            this._codeSent = true;
            this._submitting = false;
        } catch (err) {
            this._error = err.message;
            this._submitting = false;
        }
    }

    // _verifyAndAccept exchanges the emailed code for a setup token (proof of
    // email ownership), then accepts the invitation with it.
    async _verifyAndAccept() {
        this._error = '';
        this._submitting = true;
        try {
            const verifyResp = await fetch(`${getBackendUrl()}/api/v1/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // Same tenant the code was sent for, above — the invitation
                // carries it, so there is no need to resolve it again.
                body: JSON.stringify({
                    email: this._invitation.email,
                    code: this._code,
                    tenant_id: this._invitation.tenant_id,
                }),
            });
            const verifyData = await verifyResp.json();
            if (!verifyResp.ok || !verifyData.setup_token) {
                this._error = verifyData.message || 'Invalid or expired code';
                this._submitting = false;
                return;
            }

            const response = await fetch(`${getBackendUrl()}/api/v1/accept-invitation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    invitation_id: this._invitation.id,
                    username: this._username,
                    setup_token: verifyData.setup_token,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                this._error = data.message || 'Failed to accept invitation';
                this._submitting = false;
                return;
            }

            // Store tokens and redirect
            authState.setTokens(data.tokens);
            authState.user = data.user;
            authState.isAuthenticated = true;
            authState.notify();

            // The app that raised the invitation is where this person belongs
            // -- accepting is them joining it, not us (#101). Stashing the
            // destination here means navigateAfterSignIn lands them there, and
            // it survives the /consent detour on the way.
            saveInvitationDestination(this._invitation, sessionStorage);
            navigateAfterSignIn(sessionStorage, authState.consentsOutstanding);
        } catch (err) {
            this._error = err.message;
            this._submitting = false;
        }
    }

    // Accept into the account that already exists. Authenticated, so the session
    // itself is the proof the address is theirs; the API refuses a session whose
    // email is not the invited one.
    //
    // The response carries a fresh token pair because the roles it just granted
    // are only in the database until a token is minted from them — swapping the
    // session in is what makes the invitee actually authorized in the app they
    // are about to land in.
    async _acceptAsExistingUser() {
        this._submitting = true;
        this._error = '';
        try {
            const response = await fetch(
                `${getBackendUrl()}/api/v1/invitation/${this._invitation.id}/accept`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${authState.getAccessToken()}`,
                    },
                },
            );
            const data = await response.json();
            if (!response.ok) {
                this._error = data.message || 'Failed to accept invitation';
                this._submitting = false;
                return;
            }

            authState.setTokens(data.tokens);
            authState.user = data.user;
            authState.isAuthenticated = true;
            authState.notify();

            saveInvitationDestination(this._invitation, sessionStorage);
            navigateAfterSignIn(sessionStorage, authState.consentsOutstanding);
        } catch (err) {
            this._error = err.message;
            this._submitting = false;
        }
    }

    // Signed out with an account already in existence: sign in first, then come
    // back here to accept. Returning to the invitation is the point — the roles
    // come from accepting, so a sign-in that landed anywhere else would leave
    // the invitation unaccepted all over again. A bare href could not do this:
    // the navigation would beat any handler hung off it.
    _goToSignIn() {
        saveInvitationReturn(this._invitation.id, sessionStorage);
        window.location.assign(SIGN_IN_PATH);
    }

    // Signing out is the only way on, so it is the only thing offered.
    //
    // There is no "continue as who you are" for an invitation: it is an
    // assertion about one address. On the account_exists branch, proceeding as
    // the signed-in account never touches the invitation -- it walks into the
    // app as somebody who was not invited, leaving the invitation pending. On
    // the accept branch, acceptance authenticates the invited address with the
    // code emailed to it and replaces the session, so the current account is not
    // continued either; it is silently swapped, which is the surprise this card
    // exists to prevent (#105).
    async _signOutAndSwitch() {
        await authState.logout();
        window.location.reload();
    }

    // Whether the person reading this page is the person it was written for.
    _identity() {
        return invitationIdentity({
            isAuthenticated: this._authenticated,
            currentEmail: this._currentEmail,
            invitedEmail: this._invitation?.email,
        });
    }

    // Said before either exit, because both of them act on an identity and
    // neither used to mention one (#105).
    _renderIdentityMismatch() {
        return html`
            <div class="invitation-card mt-5">
                <div class="card shadow">
                    <div class="card-body p-4 text-center">
                        <i class="bi bi-person-exclamation text-warning" style="font-size: 2rem;"></i>
                        <p class="mt-3 mb-2">
                            This invitation is for <strong>${this._invitation.email}</strong>,
                            but you're signed in as <strong>${this._currentEmail}</strong>.
                        </p>
                        <p class="text-muted small mb-4">
                            It can only be used by the address it was sent to, so you'll need to
                            sign out first.
                        </p>
                        <button type="button" class="btn btn-primary btn-sm"
                            @click="${this._signOutAndSwitch}">
                            Sign out and continue as ${this._invitation.email}
                        </button>
                    </div>
                </div>
            </div>
        `;
    }

    // Everything accepting this invitation grants, named before the button that
    // grants it (#74).
    //
    // This is the only consent screen in the flow: folding the app authorization
    // into the accept means the invitee agrees to it at the moment they think
    // they are merely joining, so the screen has to say so plainly rather than
    // leaving it implied. The client line especially — it is the only grant here
    // that is not about the invitation itself, and the only one that lets
    // something act as them.
    //
    // That line names the consequence rather than the capability on purpose.
    // An invitation works for any registered client, and auth knows only the
    // client's name — not what it will go on to do with the token. Saying
    // "posting feedback under your name" would be true of the Discord bot and
    // false of every other app that ever uses this.
    _renderConsent() {
        const roleNames = this._invitation.role_names || [];
        const provider = this._invitation.provider;
        const clientName = this._invitation.client_name;

        return html`
            <div class="consent-block mb-3">
                <div class="fw-medium mb-2">By accepting, you agree to:</div>
                <ul class="mb-0 small">
                    <li>
                        Create a Dice Driven account for
                        <strong>${this._invitation.email}</strong>.
                    </li>
                    ${
                        provider
                            ? html`<li>
                        Link it to your <strong>${this._providerLabel(provider)}</strong> account.
                    </li>`
                            : ''
                    }
                    ${
                        roleNames.length
                            ? html`<li>
                        Grant you <strong>${roleNames.join(', ')}</strong> in
                        <strong>${this._invitation.app_name}</strong>.
                    </li>`
                            : ''
                    }
                    ${
                        clientName
                            ? html`<li>
                        Allow <strong>${clientName}</strong> to act as you — anything it
                        does with your account will be recorded as yours.
                    </li>`
                            : ''
                    }
                </ul>
            </div>
        `;
    }

    // Providers are stored lowercase ("discord"); the screen names them the way
    // the user knows them.
    _providerLabel(provider) {
        return provider.charAt(0).toUpperCase() + provider.slice(1);
    }

    render() {
        // Waits on the invitation AND on auth: rendering before auth settles
        // would show a signed-in viewer the anonymous card first, and let them
        // click through it before the page knows who they are (#105).
        if (this._loading || !this._authSettled) {
            return html`
                <div class="invitation-card mt-5">
                    <div class="card shadow">
                        <div class="card-body p-4 text-center">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p class="mt-3 text-muted">Loading invitation...</p>
                        </div>
                    </div>
                </div>
            `;
        }

        if (this._error && !this._invitation) {
            return html`
                <div class="invitation-card mt-5">
                    <div class="card shadow">
                        <div class="card-body p-4 text-center">
                            <i class="bi bi-exclamation-triangle text-danger" style="font-size: 2rem;"></i>
                            <p class="mt-3">${this._error}</p>
                            <a href="${SIGN_IN_PATH}" class="btn btn-primary btn-sm">Go to Sign In</a>
                        </div>
                    </div>
                </div>
            `;
        }

        if (this._invitation.status !== 'pending') {
            return html`
                <div class="invitation-card mt-5">
                    <div class="card shadow">
                        <div class="card-body p-4 text-center">
                            <p>This invitation has already been ${this._invitation.status}.</p>
                            <a href="${SIGN_IN_PATH}" class="btn btn-primary btn-sm">Go to Sign In</a>
                        </div>
                    </div>
                </div>
            `;
        }

        // Ahead of both live exits, and after the dead ones: an expired or
        // already-accepted invitation resumes nothing, so there is no identity
        // decision left to make on it (#105).
        if (this._identity() === IDENTITY_MISMATCH) {
            return this._renderIdentityMismatch();
        }

        // Checked after the status branch on purpose: for an invitation that was
        // already accepted, saying so is more informative than saying an account
        // exists — which is the reason it was accepted.
        //
        // This branch used to say "you don't need this invitation" and send the
        // person to sign in. That was wrong: signing in granted them nothing, so
        // they arrived with no permissions and the invitation stayed pending
        // forever (#114). An invitation's roles come from accepting it, and this
        // is where an existing account does that — same consent block as a new
        // one, because the same grant is being made.
        if (this._invitation.account_exists) {
            return html`
                <div class="invitation-card mt-5">
                    <div class="card shadow">
                        <div class="card-body p-4">
                            <h3 class="card-title text-center mb-3">You're Invited</h3>
                            <p class="text-center text-muted small mb-4">
                                You already have an account for
                                <strong>${this._invitation.email}</strong>, so accepting
                                just adds what this invitation grants.
                            </p>
                            ${this._renderConsent()}
                            ${this._error ? html`<div class="alert alert-danger py-2 small">${this._error}</div>` : ''}
                            <div class="d-grid">
                                ${
                                    this._authenticated
                                        ? html`
                                    <button type="button" class="btn btn-primary"
                                        ?disabled="${this._submitting}"
                                        @click="${this._acceptAsExistingUser}">
                                        ${this._submitting ? 'Accepting…' : 'Accept invitation'}
                                    </button>`
                                        : html`
                                    <button type="button" class="btn btn-primary"
                                        @click="${this._goToSignIn}">Sign in to accept</button>`
                                }
                            </div>
                        </div>
                    </div>
                </div>
            `;
        }

        return html`
            <div class="invitation-card mt-5">
                <div class="card shadow">
                    <div class="card-body p-4">
                        <h3 class="card-title text-center mb-3">You're Invited</h3>
                        <p class="text-center text-muted mb-4">
                            You've been invited to join <strong>${this._invitation.app_name}</strong>.
                        </p>

                        ${
                            this._invitation.message
                                ? html`
                            <div class="alert alert-light">${this._invitation.message}</div>
                        `
                                : ''
                        }

                        ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

                        <div class="mb-3">
                            <label class="form-label text-muted">Email</label>
                            <div class="fw-medium">${this._invitation.email}</div>
                        </div>

                        <form @submit="${this._handleSubmit}">
                            <div class="mb-3">
                                <label for="username" class="form-label">Choose a username</label>
                                <input type="text" class="form-control" id="username" required autofocus
                                    minlength="3" maxlength="30" pattern="[a-zA-Z0-9_\\-]+"
                                    ?disabled="${this._codeSent}"
                                    .value="${this._username}"
                                    @input="${(e) => (this._username = e.target.value)}">
                                <div class="form-text">3-30 characters, letters, numbers, hyphens, and underscores.</div>
                            </div>

                            ${
                                this._codeSent
                                    ? html`
                                <div class="mb-3">
                                    <label for="code" class="form-label">Verification code</label>
                                    <input type="text" class="form-control" id="code" required autofocus inputmode="numeric"
                                        .value="${this._code}"
                                        @input="${(e) => (this._code = e.target.value)}">
                                    <div class="form-text">Enter the code we emailed to ${this._invitation.email} to confirm it's you.</div>
                                </div>
                                ${this._renderConsent()}
                                <button type="submit" class="btn btn-primary w-100" ?disabled="${this._submitting || !this._code}">
                                    ${this._submitting ? 'Verifying...' : 'Verify & Accept Invitation'}
                                </button>
                            `
                                    : html`
                                <p class="text-muted small mb-3">
                                    Next we'll email a one-time code to
                                    <strong>${this._invitation.email}</strong> to confirm it's you.
                                    Look for a new email — the invitation itself doesn't contain a code.
                                </p>
                                <button type="submit" class="btn btn-primary w-100" ?disabled="${this._submitting || this._username.length < 3}">
                                    ${this._submitting ? 'Sending code...' : 'Send Verification Code'}
                                </button>
                            `
                            }
                        </form>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('invitation-view', InvitationView);
export { InvitationView };
